import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { Direction, PopupEvent } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import eq from 'webapp/helpers/eq';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import TeamsMembersListComponent from 'webapp/components/teams/members-list';
import TeamsTextComponent from 'webapp/components/teams/text';
import TeamsViewPopupListComponent from 'webapp/components/teams/view/popup-list';
import TeamsViewSectionComponent from 'webapp/components/teams/view/section/index';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type Team from 'webapp/models-custom/team';
import type TeamSection from 'webapp/models-custom/team-section';

export interface Args {
	model: Team;
}

type Action = 'edit' | 'leave' | 'delete';

type Option = PopupListItem<Action>;

export default class TeamsViewIndexComponent extends Component<Args> {
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/teams') declare mmTeamsService: Service.ModelManager.Teams;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('popup') declare popupService: Service.Popup;
	@service('navigation-main') declare navigationMainService: Service.NavigationMain;
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;

	@tracked contextOpened = false;
	@tracked isLoading = false;

	get team() {
		return this.args.model;
	}

	get id() {
		return this.team.id;
	}

	get sections() {
		return this.team?.orderedSections ?? [];
	}

	get title() {
		return this.team?.name ?? '';
	}

	get description() {
		return this.team?.description ?? '';
	}

	get colorStyle() {
		return htmlSafe(`background-color: ${this.team?.color ?? ''}`);
	}

	get hasMultiple() {
		return (this.team?.teamMembers?.length ?? 0) > 1;
	}

	get isOwnedByMe() {
		return this.team?.isOwnedByMe;
	}

	get isCurrentUserMember() {
		return this.team?.isCurrentUserMember;
	}

	get hasOwner() {
		return !!this.team?.ownerId;
	}

	get options() {
		const options: Option[] = [];

		options.push({
			icon: 'Pencil',
			text: 'Edit',
			action: 'edit',
		});

		if (!(this.isOwnedByMe && this.team?.teamMembers.length === 1)) {
			options.push({
				icon: 'Logout',
				text: 'Leave space',
				action: 'leave',
			});
		}

		if (this.isOwnedByMe) {
			options.push({
				class: 'delete',
				text: !this.hasMultiple ? 'Delete & leave the space' : 'Delete',
				action: 'delete',
				icon: 'Trash',
			});
		}

		return options;
	}

	get hasUserJoinedTeam() {
		const members = this.team?.teamMembers;
		if (!members) {
			return false;
		}

		return members.some((member) => member.id === this.currentUserService.model?.numericId);
	}

	@cached
	get showPlaceholderItem() {
		const sections = this.sections;
		// show placeholder items if there is only one section and it has no items
		return sections.length <= 1 && !sections[0]?.items.length;
	}

	get lastItemIndex() {
		return this.sections.length - 1;
	}

	get showAddSectionButton() {
		return !this.isPreview && !this.showPlaceholderItem && this.sections.length < 30;
	}

	get isPreview() {
		return !this.isCurrentUserMember;
	}

	get isReadOnly() {
		return this.isPreview || this.currentUserService.isDemo;
	}

	get userStatusText() {
		return this.isOwnedByMe ? 'Owner' : 'Member';
	}

	get isDescriptionShown() {
		return this.isCurrentUserMember || this.description.length > 0;
	}

	updateSortOrder(): void {
		const ids = this.sections.map((section) => section.numericId);

		this.requestService
			.api('PUT', `/teams/${this.team?.id}/update-order`, { ids })
			.then((_reply) => {
				//console.log('updateSortOrder reply', _reply);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	edit() {
		if (this.team) {
			this.mmTeamsService.openEditPopup(this.team);
		}
	}

	@action
	leave() {
		const team = this.team;

		if (!team) {
			return;
		}

		this.mmTeamsService.handleLeave(team);
	}

	@action
	moveUp(section: TeamSection) {
		const sections = this.sections,
			currentIndex = sections.indexOf(section);
		if (currentIndex <= 0) {
			return; // Already at the top
		}

		this.swapSectionPositions(section, sections[currentIndex - 1]!);
	}

	@action
	moveDown(section: TeamSection) {
		const sections = this.sections,
			currentIndex = sections.indexOf(section);
		if (currentIndex === -1 || currentIndex >= sections.length - 1) {
			return; // Already at the bottom
		}

		this.swapSectionPositions(section, sections[currentIndex + 1]!);
	}

	@action
	delete() {
		const team = this.team;
		if (!team) {
			return;
		}

		this.mmTeamsService.handleDelete(team);
	}

	@action
	doNothing(_text: string): void {
		return;
	}

	@action
	changeDescription(text: string): void {
		const team = this.team;
		if (!team) {
			return;
		}

		const cleanText = text.trim(),
			currentSavedText = team.description;

		if (cleanText !== currentSavedText) {
			this.analyticsService.event(TrackingEvent.TEAM_PAGE_EDITED, {
				action: ['team description'],
			});

			team.description = cleanText;
			void team.save();
		}
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	openTeamDropdown(e: PointerEvent) {
		const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>(
			'.teams-view__switcher-button',
		);

		if (!attachTo || this.popupService.isOpen(TeamsViewPopupListComponent)) {
			return;
		}

		const popup = this.popupService.createContext(TeamsViewPopupListComponent, attachTo, {
			directions: [Direction.DOWN, Direction.UP],
		});

		return popup.open({
			excludeId: this.team?.numericId ?? undefined,
		});
	}

	@action
	openOptionsDropdown(e: PointerEvent) {
		if (this.contextOpened) {
			return;
		}

		const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>('.teams-view__options');

		const popup = this.popupService.createListPopup(attachTo);

		if (popup) {
			popup.open({
				itemsList: this.options,
				doAction: (actionName?: string) => {
					if (actionName) {
						this.doOptionsAction(actionName);
					}
				},
			});

			this.contextOpened = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.contextOpened = false;
				}
			});
		}
	}

	@action
	joinTeam() {
		const team = this.team;
		if (this.hasUserJoinedTeam || !team) {
			return;
		}

		this.mmTeamsService.joinTeam(team);
	}

	@action
	addSection() {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		const team = this.team;

		if (!team || this.isLoading) {
			return;
		}

		this.analyticsService.event(TrackingEvent.TEAM_PAGE_EDITED, {
			action: ['section added'],
		});

		this.isLoading = true;
		void this.mmTeamSectionsService.createNew(team.id!).finally(() => {
			this.isLoading = false;
		});
	}

	private swapSectionPositions(section: TeamSection, otherSection: TeamSection): void {
		const tempPosition = section.position;
		section.position = otherSection.position;
		otherSection.position = tempPosition;

		// we won't be sending save
		section.applyBufferedChanges(['position']);
		otherSection.applyBufferedChanges(['position']);

		this.updateSortOrder();
	}
	<template>
		<div class="bottom-padding">
			<div class="teams-view {{if this.isDescriptionShown 'teams-view--description-shown'}}">
				<div class="teams-view__header">
					<div class="teams-view__header-left">
						<div class="teams-view__icon" style={{this.colorStyle}}>
							<CommonUiIconComponent @name="SpacesFill" @size="md" />
						</div>
						<div class="teams-view__title">
							{{this.title}}
							{{#if this.team.isClosed}}
								<ToolTipWithIconComponent
									@position="top"
									@text="This is a closed team"
									@iconName="Lock"
								/>
							{{else if this.team.isPrivate}}
								<ToolTipWithIconComponent
									@position="top"
									@text="This is a private team"
									@iconName="EyeClosed"
								/>
							{{/if}}
						</div>
						<div
							role="button"
							class="teams-view__switcher-button"
							{{on "click" this.openTeamDropdown}}
						>
							<CommonUiIconComponent @name="ChevronDown" @size="sm" />
						</div>
					</div>

					<div class="teams-view__header-right">
						{{#unless this.hasOwner}}
							<ToolTipWithIconComponent
								@position="top"
								@text="No owner assigned"
								@iconName="Alert"
								@maxWidth={{260}}
							/>
						{{/unless}}

						<div
							role="button"
							class="teams-view__status teams-view__status--{{if
									this.hasUserJoinedTeam
									'member'
									'join'
								}}"
							{{on "click" this.joinTeam}}
						>
							{{#if this.hasUserJoinedTeam}}
								{{this.userStatusText}}
							{{else}}
								<CommonUiIconComponent @name="Plus" @size="sm" />
								Join space
							{{/if}}
						</div>
						<TeamsMembersListComponent
							@model={{this.team}}
							@isPreview={{this.isPreview}}
						/>
						{{#if this.hasUserJoinedTeam}}
							<div
								role="button"
								class="teams-view__options"
								{{on "click" this.openOptionsDropdown}}
							>
								<CommonUiIconComponent @name="Ellipsis" @size="sm" />
							</div>
						{{/if}}
					</div>
				</div>

				{{#if this.isDescriptionShown}}
					<div
						class="teams-view__description teams-html-editor
							{{if this.isReadOnly 'teams-html-editor--readonly'}}"
					>
						<TeamsTextComponent
							@placeholder="Click to add description..."
							@text={{this.description}}
							@onChange={{this.doNothing}}
							@onFocusOut={{this.changeDescription}}
							@isPreview={{this.isReadOnly}}
						/>
					</div>
				{{/if}}

				<div class="teams-view__sections">
					{{#each this.sections as |section index|}}
						<TeamsViewSectionComponent
							@model={{section}}
							@isPreview={{this.isPreview}}
							@isPlaceholder={{this.showPlaceholderItem}}
							@moveUp={{this.moveUp}}
							@moveDown={{this.moveDown}}
							@isFirst={{eq index 0}}
							@isLast={{eq index this.lastItemIndex}}
						/>
					{{/each}}
				</div>

				{{#if this.showAddSectionButton}}
					<div
						role="button"
						class="teams-view__add-section-button"
						{{on "click" this.addSection}}
					>
						{{#if this.isLoading}}
							<BarLoaderComponent />
						{{else}}
							<CommonUiIconComponent @name="Plus" @size="sm" />Add section
						{{/if}}
					</div>
				{{/if}}
			</div>
		</div>
	</template>
}
